// client/src/components/pages/RouteIntelligence/index.tsx (converted from .js, ST-00218 P2)
//
// Route Intelligence: the Variance / Preload / Receipt Recon monitors (route_intelligence.view)
// and the Load matching comparison (load_matching.view, ST-00218). The Settings tab is gone:
// it only held the daily digest, which was never built and was removed in ST-00229.
// The page opens with either key (config/pagePermissions); each tab shows only to the users
// whose key it needs, so a customer reviewer with only load_matching.view sees just that tab.
// `?tab=load-matching` (plus startDate / endDate / groupingIds / route) deep-links the
// comparison — the Route Summary preview links here.
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FaBrain } from 'react-icons/fa';
import VarianceMonitorDashboard from './VarianceMonitorDashboard';
import PreloadDashboard from './PreloadDashboard';
import ReceiptReconDashboard from './ReceiptReconDashboard';
import LoadMatchingTab, { type LoadMatchingTabProps } from './LoadMatching/LoadMatchingTab';
import { PageHeader } from '../../../types/commonComponents';
import { useAuth } from '../../../context/AuthContext';

type TabId = 'variance' | 'preload' | 'receiptRecon' | 'loadMatching';

const TAB_PARAM: Record<string, TabId> = {
  variance: 'variance',
  preload: 'preload',
  'receipt-recon': 'receiptRecon',
  'load-matching': 'loadMatching',
};

const tabClass = (active: boolean) =>
  `flex-1 sm:flex-none px-2.5 sm:px-4 py-1.5 text-[11px] sm:text-xs font-semibold rounded-md transition-all whitespace-nowrap ${
    active ? 'bg-surface text-slate-800 shadow-sm' : 'text-gray-400 hover:text-gray-600'
  }`;

const RouteIntelligence = () => {
  const { hasPermission } = useAuth();
  const [searchParams] = useSearchParams();
  const monitors = hasPermission('route_intelligence.view');
  const loadMatching = hasPermission('load_matching.view') || hasPermission('load_matching.manage');

  const tabs = useMemo(() => {
    const list: { id: TabId; label: string }[] = [];
    if (monitors) {
      list.push({ id: 'variance', label: 'Variance' });
      list.push({ id: 'preload', label: 'Preload' });
      list.push({ id: 'receiptRecon', label: 'Receipt Recon' });
    }
    if (loadMatching) list.push({ id: 'loadMatching', label: 'Load matching' });
    return list;
  }, [monitors, loadMatching]);

  const requested = TAB_PARAM[searchParams.get('tab') ?? ''];
  const [activeTab, setActiveTab] = useState<TabId | null>(null);
  const current: TabId | null =
    (activeTab && tabs.some((t) => t.id === activeTab) ? activeTab : null) ??
    (requested && tabs.some((t) => t.id === requested) ? requested : null) ??
    tabs[0]?.id ??
    null;

  const initial: LoadMatchingTabProps['initial'] = useMemo(() => {
    const ids = (searchParams.get('groupingIds') ?? '')
      .split(',')
      .map((s) => Number(s.trim()))
      .filter((n) => Number.isInteger(n) && n > 0);
    const route = Number(searchParams.get('route'));
    return {
      startDate: searchParams.get('startDate'),
      endDate: searchParams.get('endDate'),
      groupingIds: ids,
      route: Number.isInteger(route) && route > 0 ? route : null,
    };
    // The deep link is read once, when the page opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-slate-50">
      <PageHeader
        icon={FaBrain}
        title="Route Intelligence"
        subtitle="Variance monitoring, preload evaluation, receipt reconciliation and load matching"
      />

      <div className="p-3 sm:p-6 max-w-7xl mx-auto">
        {/* Tab bar */}
        <div
          role="tablist"
          aria-label="Route Intelligence"
          className="flex items-center gap-0.5 sm:gap-1 bg-gray-100 rounded-lg p-0.5 mb-4 sm:mb-6 w-full sm:w-fit overflow-x-auto"
        >
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={current === t.id}
              onClick={() => setActiveTab(t.id)}
              className={tabClass(current === t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>

        {current === 'variance' ? (
          <VarianceMonitorDashboard />
        ) : current === 'preload' ? (
          <PreloadDashboard />
        ) : current === 'receiptRecon' ? (
          <ReceiptReconDashboard />
        ) : current === 'loadMatching' ? (
          <LoadMatchingTab initial={initial} />
        ) : (
          <p className="text-sm text-slate-500">
            You do not have access to any Route Intelligence tab.
          </p>
        )}
      </div>
    </div>
  );
};

export default RouteIntelligence;
